import { useMemo, ReactNode } from "react";
import { VideoCallingHookProvider, VideoCallWindow, type LocalUserInfo } from "@tellescope/video-calling";
import { useAuth, useUsersMap } from "@tellescope/tanstack-query";
import { useVideoCallingStore } from "../store";

export interface VideoCallingProviderProps {
  children: ReactNode;
}

/**
 * VideoCallingProvider - Provides video calling context and renders the VideoCallWindow.
 *
 * This provider should wrap authenticated content (inside ProtectedRoute) and provides:
 * - The video calling hook context (injected from app store)
 * - The session for API calls
 * - The VideoCallWindow component for managing video calls
 *
 * @example
 * ```tsx
 * // In ProtectedRoute.tsx
 * export const ProtectedRoute = ({ children }) => {
 *   // ... auth checks ...
 *   return (
 *     <CallingProvider>
 *       <VideoCallingProvider>{children}</VideoCallingProvider>
 *     </CallingProvider>
 *   );
 * };
 * ```
 */
export function VideoCallingProvider({ children }: VideoCallingProviderProps) {
  const { session, currentUser } = useAuth();
  const videoCalling = useVideoCallingStore();
  const { isVisible, meetingMeta, participants } = videoCalling;

  // Get user IDs from participants for name resolution
  const participantIds = useMemo(() => participants.map((p) => p.identity).filter(Boolean), [participants]);

  // Load users for participant name resolution
  const usersById = useUsersMap(participantIds);

  // Resolve participant identity to display name
  const resolveParticipantIdentity = useMemo(() => {
    return (identity: string) => {
      const user = usersById[identity];
      if (user) {
        return user.fname && user.lname ? `${user.fname} ${user.lname}` : user.email;
      }
      return undefined;
    };
  }, [usersById]);

  // Create local user info from current user for avatar display
  const localUser: LocalUserInfo | undefined = useMemo(() => {
    if (!currentUser) return undefined;
    return {
      fname: currentUser.fname,
      lname: currentUser.lname,
      avatar: currentUser.avatar,
    };
  }, [currentUser]);

  // Determine user role based on meeting metadata
  const userRole = meetingMeta.isHost ? "host" : "participant";
  const showEndForAll = meetingMeta.isHost;

  return (
    // Pass the actual Zustand hook so VideoCallWindow can subscribe to the store properly
    <VideoCallingHookProvider useVideoCalling={useVideoCallingStore} session={session}>
      {children}
      {/* Only render VideoCallWindow when visible to avoid mounting/unmounting issues */}
      {isVisible && (
        <VideoCallWindow
          userRole={userRole}
          showEndForAll={showEndForAll}
          localUser={localUser}
          resolveParticipantIdentity={resolveParticipantIdentity}
        />
      )}
    </VideoCallingHookProvider>
  );
}
